import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { wholesaleMarketClient } from '@/api'
import { config } from '@/config'

const BASE_ROUTE = config.links.library.wholesaleMarket

export const useWholesaleDataset = (datasetId: number) => {
  const { showSuccess } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const QUERY_KEY = [...wholesaleMarketClient.QUERY_KEY, datasetId]

  const getWholesaleDatasetQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => wholesaleMarketClient.getDetails(datasetId),
    enabled: !!datasetId
  })

  const uploadDatasetFileMutation = useMutation({
    mutationFn: ({ fileType, file }: { fileType: string; file: File }) =>
      wholesaleMarketClient.uploadDatasetFile({
        datasetId: datasetId,
        fileType,
        file
      }),
    onSuccess: async (_, variables) => {
      showSuccess(`File ${variables.file.name} successfully uploaded`)
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const removeDatasetFileMutation = useMutation({
    mutationFn: wholesaleMarketClient.removeDatasetFile,
    onSuccess: async () => {
      showSuccess(`Dataset file successfully removed`)
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const cloneDatasetMutation = useMutation({
    mutationFn: (datasetName: string) =>
      wholesaleMarketClient.cloneDataset({ datasetId, name: datasetName }),
    onSuccess: clonedDatasetId => {
      showSuccess('Dataset successfully cloned')
      navigate(`${BASE_ROUTE}/${clonedDatasetId}`)
    }
  })

  const removeDatasetMutation = useMutation({
    mutationFn: () => wholesaleMarketClient.removeDataset(datasetId),
    onSuccess: () => {
      showSuccess('Dataset successfully removed')
      navigate(BASE_ROUTE)
    }
  })

  const lockDatasetMutation = useMutation({
    mutationFn: () => wholesaleMarketClient.lockDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully locked')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const archiveDatasetMutation = useMutation({
    mutationFn: () => wholesaleMarketClient.archiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully archived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const unArchiveDatasetMutation = useMutation({
    mutationFn: () => wholesaleMarketClient.unArchiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully unarchived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    datasetInfo: getWholesaleDatasetQuery.data,
    isFetching:
      getWholesaleDatasetQuery.isFetching &&
      !getWholesaleDatasetQuery.isRefetching,
    uploadDatasetFile: uploadDatasetFileMutation.mutate,
    isDatasetFileUploading: uploadDatasetFileMutation.isPending,
    removeDatasetFile: removeDatasetFileMutation.mutate,
    cloneDataset: cloneDatasetMutation.mutate,
    isCloneDatasetInProgress: cloneDatasetMutation.isPending,
    removeDataset: removeDatasetMutation.mutate,
    lockDataset: lockDatasetMutation.mutate,
    archiveDataset: archiveDatasetMutation.mutate,
    unArchiveDataset: unArchiveDatasetMutation.mutate,
    getDownloadFileUrl: (fileId: number | undefined) =>
      !fileId
        ? undefined
        : wholesaleMarketClient.downloadWholesaleMarketFileUrl(fileId)
  }
}
